import React, { useState, useEffect } from 'react';
import './App.css';
import logoImage from './logo.png';
import playIcon from './play-icon.png';
import filterIcon from './filter_icon.png';
import emptyHeartIcon from './empty_heart.png';
import fullHeartIcon from './full_heart.png';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { auth, db } from './firebase'; // Firebase initialization
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, updateDoc, arrayUnion, getDoc, setDoc } from 'firebase/firestore';
import Select from 'react-select';
import CloseIcon from '@mui/icons-material/Close';


function MainApp() {
  const [yogaVideos, setYogaVideos] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedLength, setSelectedLength] = useState('All');
  const [selectedGoal, setSelectedGoal] = useState('All');
  const [selectedBodyFocus, setSelectedBodyFocus] = useState('All');
  const [selectedNeed, setSelectedNeed] = useState('All');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showSavedVideos, setShowSavedVideos] = useState(false);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');


  if (loading) {
    console.log('Loading...'); // Example of using the 'loading' variable
  }
  
  // Detect if the device is mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Firebase: Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          savedVideos: [], // Saved videos will be fetched from Firestore
        });
        // Fetch saved videos from Firestore
        fetchSavedVideos(firebaseUser.uid);
      } else {
        setUser(null); // User logged out
      }
    });

    return () => unsubscribe(); // Cleanup on unmount
  }, []);

  // Fetch saved videos for logged-in users from Firestore
  const fetchSavedVideos = async (uid) => {
    const docRef = doc(db, 'users', uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      setUser((prevUser) => ({
        ...prevUser,
        savedVideos: docSnap.data().savedVideos || [],
      }));
    }
  };

  // Fetch yoga video data from Google Sheets
  useEffect(() => {
    const sheetApiUrl = `https://sheets.googleapis.com/v4/spreadsheets/1wzEBR9vzB-F8tcza-14ODbDlDnijqDnyOzvejWstEaE/values/Sheet1?key=${process.env.REACT_APP_GOOGLE_API_KEY}`;
    const fetchData = async () => {
      try {
        const response = await fetch(sheetApiUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const parsedVideos = data.values.slice(1).map((row) => ({
          id: row[0],
          title: row[0],
          url: row[1],
          thumbnail: row[2],
          level: row[3],
          type: row[4],
          duration: row[5],
          creator: row[6],
          goal: row[7],
          bodyFocus: row[8],
          needs: row[9],
          saved: false,
        }));
        setYogaVideos(parsedVideos);
      } catch (error) {
        console.error('Error fetching Google Sheets data:', error);
        setYogaVideos([]);
      }
    };
    fetchData();
  }, []);

  // Handle saving or removing a video in Firestore
  const handleSaveVideo = async (video, e) => {
    e.stopPropagation();
    setLoading(true);
  
    if (!user) {
      setShowLoginModal(true); // Show login modal if not authenticated
      setLoading(false);
      return;
    }
  
    const userRef = doc(db, 'users', user.uid);
  
    try {
      // Check if the user document exists
      const userDoc = await getDoc(userRef);
  
      if (!userDoc.exists()) {
        // If the document doesn't exist, create it with an empty array for savedVideos
        await setDoc(userRef, { savedVideos: [] });
      }
  
      if (user.savedVideos.some((savedVideo) => savedVideo.id === video.id)) {
        // Remove the video
        await updateDoc(userRef, {
          savedVideos: user.savedVideos.filter((savedVideo) => savedVideo.id !== video.id),
        });
        setUser((prevUser) => ({
          ...prevUser,
          savedVideos: prevUser.savedVideos.filter((savedVideo) => savedVideo.id !== video.id),
        }));
      } else {
        // Save the video
        await updateDoc(userRef, {
          savedVideos: arrayUnion(video),
        });
        setUser((prevUser) => ({
          ...prevUser,
          savedVideos: [...prevUser.savedVideos, video],
        }));
      }
    } catch (error) {
      console.error('Error saving/removing video:', error);
    } finally {
      setLoading(false);
    }
  };
  

  // Handle login with Firebase
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setShowLoginModal(false);
    } catch (error) {
      setError('Login failed. Please check your credentials.');
      console.error(error);
    }
  };

  // Handle sign up with Firebase
  const handleSignUp = async (e) => {
    e.preventDefault();
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      setShowSignupModal(false);
    } catch (error) {
      setError('Signup failed. Please try again.');
      console.error(error);
    }
  };

  // Filter options for React Select
  const levelOptions = [
    { value: 'All', label: 'Level - All' },
    { value: 'Beginner', label: 'Beginner' },
    { value: 'Intermediate', label: 'Intermediate' },
    { value: 'Advanced', label: 'Advanced' },
  ];

  const typeOptions = [
    { value: 'All', label: 'Type - All' },
    { value: 'Hatha Yoga', label: 'Hatha Yoga' },
    { value: 'Vinyasa Yoga', label: 'Vinyasa Yoga' },
    { value: 'Power Yoga', label: 'Power Yoga' },
  ];

  const lengthOptions = [
    { value: 'All', label: 'Duration - All' },
    { value: '< 20 min', label: '< 20 min' },
    { value: '20-40 min', label: '20-40 min' },
    { value: '> 40 min', label: '> 40 min' },
  ];

  const goalOptions = [
    { value: 'All', label: 'Goal - All' },
    { value: 'Flexibility', label: 'Flexibility' },
    { value: 'Mindfulness & Meditation', label: 'Mindfulness & Meditation' },
    { value: 'Weight Loss', label: 'Weight Loss' },
    { value: 'Stress Relief', label: 'Stress Relief' },
    { value: 'Strength Building', label: 'Strength Building' },
  ];

  const bodyFocusOptions = [
    { value: 'All', label: 'Body Focus - All' },
    { value: 'Full Body', label: 'Full Body' },
    { value: 'Core', label: 'Core' },
    { value: 'Upper Body', label: 'Upper Body' },
    { value: 'Lower Body', label: 'Lower Body' },
  ];

  const needOptions = [
    { value: 'All', label: 'Group - All' },
    { value: 'Athletes', label: 'Athletes' },
    { value: 'Runners', label: 'Runners' },
    { value: 'Postpartum', label: 'Postpartum' },
    { value: 'Prenatal', label: 'Prenatal' },
    { value: 'Senior', label: 'Senior' },
    
  ];

  // Filtering logic for videos based on selected filters
  const filteredVideos = (showSavedVideos && user?.savedVideos?.length ? user.savedVideos : yogaVideos).filter((video) => {
    const videoDuration = parseInt(video.duration.split(' ')[0], 10);
    const isLevelMatch = selectedLevel === 'All' || video.level === selectedLevel;
    const isTypeMatch = selectedType === 'All' || video.type === selectedType;
    const isLengthMatch =
      selectedLength === 'All' ||
      (selectedLength === '< 20 min' && videoDuration < 20) ||
      (selectedLength === '20-40 min' && videoDuration >= 20 && videoDuration <= 40) ||
      (selectedLength === '> 40 min' && videoDuration > 40);
    const isGoalMatch = selectedGoal === 'All' || video.goal === selectedGoal;
    const isBodyFocusMatch = selectedBodyFocus === 'All' || video.bodyFocus === selectedBodyFocus;
    const isNeedMatch = selectedNeed === 'All' || video.needs === selectedNeed;

    return isLevelMatch && isTypeMatch && isLengthMatch && isGoalMatch && isBodyFocusMatch && isNeedMatch;
  });

  return (
    <div className="App">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          {isMobile && (
            <button className="filter-button" onClick={() => setFilterModalOpen(true)}>
              <img src={filterIcon} alt="Filter" className="filter-icon" />
            </button>
          )}
          <div className="logo">
            <img src={logoImage} alt="Logo" className="logo-img" />
          </div>
          <div className="user-area">
            {user ? (
              <div className="user-info">
                <span>Welcome, {user.email}!</span>
                <button onClick={() => setShowSavedVideos(!showSavedVideos)}>
                  {showSavedVideos ? 'Show All Videos' : 'Saved Videos'}
                </button>
                <button onClick={() => signOut(auth)}>Logout</button>
              </div>
            ) : (
              <div className="user-buttons">
                <button className="login-button" onClick={() => setShowLoginModal(true)}>
                  Login
                </button>
                <button className="signup-button" onClick={() => setShowSignupModal(true)}>
                  Sign Up
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Filters Section for Desktop */}
      {!isMobile && (
  <div className="filters-section">
  <div className="filters">
    <Select
      className="filter-dropdown"
      options={levelOptions}
      value={levelOptions.find((option) => option.value === selectedLevel)}
      onChange={(option) => setSelectedLevel(option.value)}
      placeholder="All Levels"
    />
    <Select
      className="filter-dropdown"
      options={typeOptions}
      value={typeOptions.find((option) => option.value === selectedType)}
      onChange={(option) => setSelectedType(option.value)}
      placeholder="All Types"
    />
    <Select
      className="filter-dropdown"
      options={lengthOptions}
      value={lengthOptions.find((option) => option.value === selectedLength)}
      onChange={(option) => setSelectedLength(option.value)}
      placeholder="All Lengths"
    />
    <Select
      className="filter-dropdown"
      options={goalOptions}
      value={goalOptions.find((option) => option.value === selectedGoal)}
      onChange={(option) => setSelectedGoal(option.value)}
      placeholder="All Goals"
    />
    <Select
      className="filter-dropdown"
      options={bodyFocusOptions}
      value={bodyFocusOptions.find((option) => option.value === selectedBodyFocus)}
      onChange={(option) => setSelectedBodyFocus(option.value)}
      placeholder="All Body Focus Areas"
    />
    <Select
      className="filter-dropdown"
      options={needOptions}
      value={needOptions.find((option) => option.value === selectedNeed)}
      onChange={(option) => setSelectedNeed(option.value)}
      placeholder="All Specific Needs"
    />
  </div>
</div>

)}
   {/* Filter Modal for Mobile */}
{filterModalOpen && (
  <div className="popup-overlay" onClick={() => setFilterModalOpen(false)}>
    <div className="popup-content" onClick={(e) => e.stopPropagation()}>
      <div className="popup-header">
        <h2>Filters</h2>
        <CloseIcon
          className="close-icon"
          onClick={() => setFilterModalOpen(false)}
          style={{ cursor: 'pointer' }}
        />
      </div>

      {/* User Section */}
      {user ? (
        <div className="user-section">
          <span>Welcome, {user.email}!</span>
          <button
            className="saved-videos-button"
            onClick={() => {
              setShowSavedVideos(!showSavedVideos);
              setFilterModalOpen(false);
            }}
          >
            {showSavedVideos ? 'Show All Videos' : 'Saved Videos'}
          </button>
          <button
            className="logout-button"
            onClick={() => {
              signOut(auth);
              setFilterModalOpen(false);
            }}
          >
            Logout
          </button>
        </div>
      ) : (
        <div className="user-section">
          <button
            className="login-button"
            onClick={() => {
              setShowLoginModal(true);
              setFilterModalOpen(false);
            }}
          >
            Login
          </button>
          <button
            className="signup-button"
            onClick={() => {
              setShowSignupModal(true);
              setFilterModalOpen(false);
            }}
          >
            Sign Up
          </button>
        </div>
      )}

      {/* Filters Section */}
      <div className="filters-container">

      <Select
        options={levelOptions}
        value={levelOptions.find((option) => option.value === selectedLevel)}
        onChange={(option) => setSelectedLevel(option.value)}
        placeholder="Select Level"
      />
      <Select
        options={typeOptions}
        value={typeOptions.find((option) => option.value === selectedType)}
        onChange={(option) => setSelectedType(option.value)}
        placeholder="Select Type"
      />
      <Select
        options={lengthOptions}
        value={lengthOptions.find((option) => option.value === selectedLength)}
        onChange={(option) => setSelectedLength(option.value)}
        placeholder="Select Length"
      />
      <Select
        options={goalOptions}
        value={goalOptions.find((option) => option.value === selectedGoal)}
        onChange={(option) => setSelectedGoal(option.value)}
        placeholder="Select Goal"
      />
      <Select
        options={bodyFocusOptions}
        value={bodyFocusOptions.find((option) => option.value === selectedBodyFocus)}
        onChange={(option) => setSelectedBodyFocus(option.value)}
        placeholder="Select Body Focus"
      />
      <Select
        options={needOptions}
        value={needOptions.find((option) => option.value === selectedNeed)}
        onChange={(option) => setSelectedNeed(option.value)}
        placeholder="Select Specific Need"
      />
      
      {/* Apply Button */}
      <button
        className="apply-filters-button"
        onClick={() => setFilterModalOpen(false)}
      >
        Apply
      </button>
    </div>
  </div>
  </div>

)}



      {/* Video Grid */}
      <div className="video-grid">
        {filteredVideos.length > 0 ? (
          filteredVideos.map((video, index) => (
            <div key={index} className="video-card" onClick={(e) => { e.stopPropagation(); setSelectedVideo(video); }}>
              <div
                className="heart-icon-container"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent click on heart icon from triggering video card click
                  handleSaveVideo(video, e);
                }}
              >
                <img
                  src={user?.savedVideos?.some((savedVideo) => savedVideo.id === video.id) ? fullHeartIcon : emptyHeartIcon}
                  alt="Save Video"
                  className="heart-icon"
                />
              </div>
              <h2>{video.title}</h2>
              <p>
                <strong>Creator:</strong> {video.creator}
              </p>
              <div className="tags">
                <span className="tag tag-duration">{video.duration}</span>
                <span className="tag tag-level">{video.level}</span>
                <span className="tag tag-type">{video.type}</span>
                <span className="tag tag-goal">{video.goal}</span>
                <span className="tag tag-body-focus">{video.bodyFocus}</span>
              </div>
              <div className="thumbnail">
                <img src={video.thumbnail} alt={video.title} />
                <div className="play-icon-overlay">
                  <img src={playIcon} alt="Play" className="play-icon" />
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-videos">No videos found.</div>
        )}
      </div>

      {/* Video Popup for Selected Video */}
      {selectedVideo && (
        <div className="popup-overlay" onClick={() => setSelectedVideo(null)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <CloseIcon
                className="close-icon"
                onClick={() => setSelectedVideo(null)}
                style={{ cursor: 'pointer', position: 'absolute', top: '10px', right: '10px' }}
              />
            </div>
            <h2>{selectedVideo.title}</h2>
            <p style={{ textAlign: 'center' }}>
              <strong>Creator:</strong> {selectedVideo.creator}
            </p>
            <div className="tags" style={{ textAlign: 'center' }}>
              <span className="tag tag-duration">{selectedVideo.duration}</span>
              <span className="tag tag-level">{selectedVideo.level}</span>
              <span className="tag tag-type">{selectedVideo.type}</span>
              <span className="tag tag-goal">{selectedVideo.goal}</span>
              <span className="tag tag-body-focus">{selectedVideo.bodyFocus}</span>
            </div>
            <iframe
              width="100%"
              height="400px"
              src={`https://www.youtube.com/embed/${selectedVideo.url.split('v=')[1]}`}
              title={selectedVideo.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
            
          </div>
        </div>
      )}

      <footer className="footer">
        <p>Yoga Video Curation &copy; 2024. All rights reserved.</p>
      </footer>

      {/* Login Modal */}
{showLoginModal && (
  <div className="popup-overlay">
    <div className="popup-content">
      <div className="popup-header">
        <h2>Login</h2>
        <CloseIcon
          className="close-icon"
          onClick={() => setShowLoginModal(false)}
          style={{ cursor: 'pointer', position: 'absolute', top: '10px', right: '10px' }}
        />
      </div>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      <form onSubmit={handleLogin}>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button type="submit">Login</button>
      </form>
    </div>
  </div>
)}

{/* Signup Modal */}
{showSignupModal && (
  <div className="popup-overlay">
    <div className="popup-content">
      <div className="popup-header">
        <h2>Sign Up</h2>
        <CloseIcon
          className="close-icon"
          onClick={() => setShowSignupModal(false)}
          style={{ cursor: 'pointer', position: 'absolute', top: '10px', right: '10px' }}
        />
      </div>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      <form onSubmit={handleSignUp}>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button type="submit">Sign Up</button>
      </form>
    </div>
  </div>
)}
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<MainApp />} />
        {/* Removed UserProfile and UserManagement */}
      </Routes>
    </Router>
  );
}

export default App;